import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { createElement, useEffect, useId, useMemo, useState } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import classnames from "classnames";
import { useDebounce } from "../../hooks/index.js";
import { CircularProgress } from "../../CircularProgress/index.js";
import { Box } from "../../Box/index.js";
import { Badge, Label, Preloader } from "../common/index.js";
import { Icon } from "../../Icon/index.js";
import { useAutocompleteReducer } from "../hooks/index.js";
import { Typography } from "../../Typography/index.js";
import { ListBox } from "./ListBox.js";
import { useStyles } from "./AutocompleteInput.styles.js";
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) resolve(value);
    else Promise.resolve(value).then(_next, _throw);
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(void 0);
        });
    };
}
function _define_property(obj, key, value) {
    if (key in obj) Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
    });
    else obj[key] = value;
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = null != arguments[i] ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if ("function" == typeof Object.getOwnPropertySymbols) ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
            return Object.getOwnPropertyDescriptor(source, sym).enumerable;
        }));
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function AutocompleteInput_ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) symbols = symbols.filter(function(sym) {
            return Object.getOwnPropertyDescriptor(object, sym).enumerable;
        });
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = null != source ? source : {};
    if (Object.getOwnPropertyDescriptors) Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    else AutocompleteInput_ownKeys(Object(source)).forEach(function(key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
    return target;
}
const DEBOUNCE_TIMEOUT = 200;
const AutocompleteInput = ({ id, label, labelIcon, search, value, onChange, onFocus, onBlur, placeholder, name, required = false, clearable = false, disabled = false, limit = 50, loadItems, renderOption, tooltip, error = false, helperText, loading = false, additionalFilters, minInputValueLength = 1, testId, withoutMargin = false, onInputValueChange, getSearchOptionLabel })=>{
    const { classes } = useStyles();
    const reactId = useId();
    const inputId = useMemo(()=>null != id ? id : reactId, [
        id,
        reactId
    ]);
    const { items, status, hasMore, requestItems, resetItems } = useAutocompleteReducer(loadItems);
    const [isLoading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [filter, setFilter] = useState({
        limit,
        offset: 0,
        query: ''
    });
    const [open, setOpen] = useState(false);
    const isItemsLoading = isLoading || 'loading' === status;
    const debouncedInputValue = useDebounce(inputValue, DEBOUNCE_TIMEOUT);
    const isThereSomeGroup = items.some((option)=>option.group);
    const autocompleteOpenHandler = ()=>{
        setOpen(true);
    };
    const autocompleteCloseHandler = ()=>{
        setOpen(false);
    };
    const autocompleteFocusHandler = ()=>{
        setInputValue('');
        null == onFocus || onFocus();
    };
    const autocompleteGetInputValue = ()=>{
        if (!loading && !open && !inputValue) return ((null == selectedItem ? void 0 : selectedItem.label) || '').toString().split('\n').join(', ');
        return inputValue;
    };
    const autocompleteChangeHandler = (_, nextValue)=>{
        onChange((null == nextValue ? void 0 : nextValue.value) || '', nextValue);
        setSelectedItem(nextValue);
        resetItems();
    };
    const autocompleteInputChangeHandler = (_, nextValue, reason)=>{
        if ('reset' === reason) {
            setInputValue('');
            setFilter(_object_spread_props(_object_spread({}, filter), {
                offset: 0,
                query: ''
            }));
            return;
        }
        if ('clear' === reason) setSelectedItem(null);
        if ('input' === reason) {
            setLoading(true);
            setFilter(_object_spread_props(_object_spread({}, filter), {
                offset: 0,
                query: nextValue
            }));
        }
        if (onInputValueChange) onInputValueChange(nextValue);
        setInputValue(nextValue);
    };
    const autocompleteGetOptionSelected = (option)=>option.value === value;
    const autocompleteGetOptionLabel = (option)=>option.label;
    const requestNextItems = ()=>{
        null == requestItems || requestItems(_object_spread({}, filter, additionalFilters), 'get');
        setFilter(_object_spread_props(_object_spread({}, filter), {
            offset: filter.offset + filter.limit
        }));
    };
    useEffect(()=>{
        if (!open || '' !== debouncedInputValue && debouncedInputValue.length < minInputValueLength) return;
        const abortRequest = null == requestItems ? void 0 : requestItems(_object_spread_props(_object_spread({}, filter, additionalFilters), {
            offset: 0,
            query: debouncedInputValue
        }), 'search');
        setFilter(_object_spread_props(_object_spread({}, filter), {
            offset: filter.limit
        }));
        setLoading(false);
        return ()=>{
            null == abortRequest || abortRequest();
        };
    }, [
        open,
        debouncedInputValue
    ]);
    useEffect(()=>{
        if (!open && !value) {
            setInputValue('');
            setSelectedItem(null);
        }
    }, [
        open,
        value
    ]);
    useEffect(()=>{
        (()=>_async_to_generator(function*() {
                if (value && !inputValue) {
                    const itemsById = yield null == loadItems ? void 0 : loadItems(_object_spread({
                        limit: 50,
                        offset: 0,
                        query: '',
                        id: value
                    }, additionalFilters));
                    const itemById = null == itemsById ? void 0 : itemsById.find((item)=>item.value === value);
                    setSelectedItem(itemById || null);
                }
            })())();
    }, []);
    const customListboxProps = {
        dataLength: items.length,
        next: requestNextItems,
        hasMore,
        loading: isItemsLoading,
        long: !!renderOption
    };
    return /*#__PURE__*/ jsxs(Box, {
        className: classnames((!withoutMargin || !search) && classes.autocompleteInput, 'aqa_autocomplete_input', classes.autocompleteInputContainer),
        children: [
            !search && label && /*#__PURE__*/ jsx(Label, {
                label: label,
                required: required,
                tooltip: tooltip,
                icon: labelIcon,
                inputId: inputId,
                className: "aqa_autocomplete_input_label"
            }),
            /*#__PURE__*/ jsx(Autocomplete, _object_spread_props(_object_spread({
                blurOnSelect: true,
                "data-testid": testId,
                classes: {
                    paper: classes.list,
                    option: classnames(classes.option, {
                        [classes.defaultOption]: !renderOption
                    })
                },
                open: open,
                filterOptions: (options)=>options,
                onOpen: autocompleteOpenHandler,
                value: value ? selectedItem : null,
                disableClearable: !clearable || !value,
                onClose: autocompleteCloseHandler,
                onChange: autocompleteChangeHandler,
                onFocus: autocompleteFocusHandler,
                onBlur: onBlur,
                disabled: disabled || loading,
                inputValue: autocompleteGetInputValue(),
                onInputChange: autocompleteInputChangeHandler,
                slotProps: {
                    listbox: _object_spread({
                        component: ListBox
                    }, customListboxProps)
                },
                renderOption: renderOption && ((props, option)=>/*#__PURE__*/ createElement("li", _object_spread_props(_object_spread({}, props), {
                        key: option.value
                    }), renderOption(option)))
            }, isThereSomeGroup ? {
                groupBy: ({ group })=>{
                    if (group) return group;
                    return 'Заявки без группировки по коллекциям';
                }
            } : {}), {
                renderGroup: (params)=>/*#__PURE__*/ jsxs(Fragment, {
                        children: [
                            /*#__PURE__*/ jsx(Box, {
                                px: "16",
                                pt: "8",
                                pb: "4",
                                children: /*#__PURE__*/ jsx(Typography, {
                                    component: "span",
                                    variant: "caption",
                                    color: "text.light.tertirary",
                                    children: params.group
                                })
                            }),
                            /*#__PURE__*/ jsx(Box, {
                                children: params.children
                            })
                        ]
                    }),
                isOptionEqualToValue: autocompleteGetOptionSelected,
                getOptionLabel: search ? getSearchOptionLabel : autocompleteGetOptionLabel,
                options: !filter.offset && isItemsLoading ? [] : items,
                loading: isItemsLoading,
                loadingText: /*#__PURE__*/ jsx(Box, {
                    display: "flex",
                    justifyContent: "center",
                    p: "16",
                    children: /*#__PURE__*/ jsx(CircularProgress, {
                        size: 30
                    })
                }),
                noOptionsText: "Ничего не найдено...",
                popupIcon: search ? null : /*#__PURE__*/ jsx(Box, {
                    m: "4",
                    children: /*#__PURE__*/ jsx(Icon, {
                        name: "arrow_triangle_down",
                        size: "16",
                        color: "text.dark.secondary"
                    })
                }),
                clearIcon: search ? /*#__PURE__*/ jsx(Icon, {
                    name: "cross_large_outline",
                    size: "20",
                    color: "original.brand.primary"
                }) : /*#__PURE__*/ jsx(Icon, {
                    name: "dismiss_substract",
                    color: "stroke.secondary",
                    size: "24"
                }),
                renderInput: (params)=>{
                    var _selectedItem_badge, _selectedItem_badge1, _selectedItem_badge2;
                    return /*#__PURE__*/ jsx(TextField, _object_spread(_object_spread_props(_object_spread({}, params), {
                        id: inputId,
                        name: name,
                        classes: {
                            root: `${classes.input} ${search ? classes.search : ''} aqa_select`
                        },
                        variant: "outlined",
                        placeholder: loading ? 'Загрузка...' : placeholder || label,
                        error: error,
                        helperText: helperText
                    }), loading ? {
                        slotProps: {
                            input: {
                                endAdornment: /*#__PURE__*/ jsx(Preloader, {})
                            }
                        }
                    } : {
                        slotProps: {
                            input: _object_spread_props(_object_spread({}, params.InputProps), {
                                startAdornment: search ? /*#__PURE__*/ jsx(Icon, {
                                    name: "search_outline",
                                    size: "20",
                                    color: "text.light.tertirary"
                                }) : (null == selectedItem ? void 0 : selectedItem.badge) && !open && !inputValue && /*#__PURE__*/ jsx(Box, {
                                    pl: "16",
                                    mr: "-8",
                                    children: /*#__PURE__*/ jsx(Badge, {
                                        color: null == (_selectedItem_badge = selectedItem.badge) ? void 0 : _selectedItem_badge.color,
                                        title: null == (_selectedItem_badge1 = selectedItem.badge) ? void 0 : _selectedItem_badge1.title,
                                        description: null == (_selectedItem_badge2 = selectedItem.badge) ? void 0 : _selectedItem_badge2.description
                                    })
                                })
                            })
                        }
                    }));
                }
            }))
        ]
    });
};
export { AutocompleteInput };
